import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import axios from 'axios';
import { Calendar, Search, Bell, MessageSquare, Calendar as CalendarIcon, User, Plus, TrendingUp, Home, Users, Building, FileText, Settings, ChevronDown, MoreHorizontal, Eye, Edit, Trash2, MapPin, Star, Clock, DollarSign, BarChart3, UserCheck, Award, Target, AlertTriangle, Anchor, CreditCard, Heart, Utensils, Shield } from 'lucide-react';

// Import des nouveaux composants
import AvailabilityManagement from './components/AvailabilityManagement';
import BankReconciliation from './components/BankReconciliation';
import CancellationPolicy from './components/CancellationPolicy';
import Commissions from './components/Commissions';
import CommunicationsTemplates from './components/CommunicationsTemplates';
import CommunicationsCenter from './components/CommunicationsCenter';
import ConciergeManagement from './components/ConciergeManagement';
import ContractsLegal from './components/ContractsLegal';
import DestinationKnowledge from './components/DestinationKnowledge';
import DisputesRefunds from './components/DisputesRefunds';
import LeadManagement from './components/LeadManagement';
import MybaWorkflow from './components/MybaWorkflow';
import OwnersManagement from './components/OwnersManagement';
import PartnersManagement from './components/PartnersManagement';
import PaymentsManagement from './components/PaymentsManagement';
// ClientPreferences removed - now accessible via ClientDetail
// PricingAdvanced removed from menu
import ReservationsCenter from './components/ReservationsCenter';
import RestaurantsManagement from './components/RestaurantsManagement';
import SettingsIntegrations from './components/SettingsIntegrations';
import StaffServices from './components/StaffServices';
import UsersManagement from './components/UsersManagement';
// VillaAdminDetail removed from menu
import VillasManagement from './components/VillasManagement';
import VillaWorkflow from './components/VillaWorkflow';
import VIPManagement from './components/VipManagement';
import YachtCommunications from './components/YachtCommunications';
import YachtsManagement from './components/YachtsManagement';
import ClientsBase from './components/ClientsBase';
import Distribution from './components/Distribution';
import AgenciesManagement from './components/AgenciesManagement';
import InvoicesManagement from './components/InvoicesManagement';
import DestinationSettings from './components/DestinationSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// KPI Card Component
const KPICard = ({ title, value, change, trend, icon: Icon, color = "gold" }) => (
  <div className="kpi-card">
    <div className="kpi-header">
      <div className={`kpi-icon ${color}`}>
        <Icon size={24} />
      </div>
      <div className="kpi-trend">
        <span className={`trend ${trend > 0 ? 'positive' : trend < 0 ? 'negative' : 'neutral'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      </div>
    </div>
    <div className="kpi-content">
      <h3 className="kpi-value">{value}</h3>
      <p className="kpi-title">{title}</p>
      <span className="kpi-change">{change}</span>
    </div>
  </div>
);

// Sidebar Component
const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeSection = location.pathname.slice(1) || 'dashboard';
  const menuSections = [
    {
      title: "Tableau de Bord",
      items: [
        { id: "dashboard", label: "Vue d'ensemble", icon: Home, badge: null }
      ]
    },
    {
      title: "Réservations",
      items: [
        { id: "reservations-center", label: "Centre de Réservations", icon: Calendar, badge: 8 },
        { id: "availability", label: "Disponibilités", icon: Calendar, badge: null }
      ]
    },
    {
      title: "Clients",
      items: [
        { id: "clients", label: "Base Clients", icon: Users, badge: null },
        { id: "vip-management", label: "VIP Management", icon: Award, badge: 324 }
      ]
    },
    {
      title: "Communications",
      items: [
        { id: "communications-center", label: "Centre de Communication", icon: MessageSquare, badge: 7 },
        { id: "communications-templates", label: "Templates", icon: FileText, badge: null }
      ]
    },
    {
      title: "Services",
      items: [
        { id: "concierge", label: "Conciergerie", icon: UserCheck, badge: 8 },
        { id: "commissions", label: "Commissions", icon: DollarSign, badge: null }
      ]
    },
    // Section Inventaire supprimée
    {
      title: "Propriétés",
      items: [
        { id: "villas-management", label: "Gestion des Villas", icon: Building, badge: 23 },
        { id: "owners-management", label: "Gestion Propriétaires", icon: Users, badge: 12 },
        { id: "agencies-management", label: "Gestion des Agences", icon: Users, badge: 4 },
        { id: "distribution", label: "Distribution", icon: Target, badge: 2654 }
      ]
    },
    {
      title: "Finances",
      items: [
        { id: "invoices-management", label: "Factures", icon: FileText, badge: 12 },
        { id: "payments-management", label: "Gestion des Paiements", icon: CreditCard, badge: 5 },
        { id: "destination-settings", label: "Paramétrage Destinations", icon: MapPin, badge: 3 },
        { id: "bank-reconciliation", label: "Réconciliation Bancaire", icon: BarChart3, badge: null }
      ]
    },
    {
      title: "Services & Personnel",
      items: [
        { id: "concierge", label: "Conciergerie", icon: Star, badge: 7 },
        { id: "staff-services", label: "Staff & Services", icon: Users, badge: 3 },
        { id: "communications-templates", label: "Communications", icon: MessageSquare, badge: null },
        { id: "communications-center", label: "Centre Communications", icon: MessageSquare, badge: 4 }
      ]
    },
    {
      title: "Administration",
      items: [
        { id: "users-management", label: "Gestion des Utilisateurs", icon: Shield, badge: 28 },
        { id: "settings-integrations", label: "Paramètres & API", icon: Settings, badge: null }
      ]
    },
    {
      title: "Workflow & Charter",
      items: [
        { id: "villa-workflow", label: "Villa Workflow", icon: Settings, badge: 5 },
        { id: "myba-workflow", label: "MYBA Charter Workflow", icon: Anchor, badge: 8 }
      ]
    },
    {
      title: "Partenaires",
      items: [
        { id: "partners-management", label: "Réseau Partenaires", icon: UserCheck, badge: 28 },
        { id: "restaurants-management", label: "Restaurants", icon: Utensils, badge: 18 }
      ]
    },
    {
      title: "Légal & Support",
      items: [
        { id: "contracts-legal", label: "Contrats & Légal", icon: FileText, badge: null },
        { id: "disputes-refunds", label: "Litiges & Remboursements", icon: AlertTriangle, badge: 3 },
        { id: "cancellation-policy", label: "Politique d'Annulation", icon: Settings, badge: null }
      ]
    },
    {
      title: "Marketing & Ventes",
      items: [
        { id: "lead-management", label: "Gestion des Prospects", icon: Target, badge: 15 },
        { id: "destination-knowledge", label: "Base de Connaissances", icon: Star, badge: null }
      ]
    },
    {
      title: "Yachts & Charter",
      items: [
        { id: "yachts-management", label: "Portfolio Yachts", icon: Anchor, badge: 89 },
        { id: "yacht-communications", label: "Communications Yachts", icon: MessageSquare, badge: 4 }
      ]
    }
  ];

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <h1 className="logo">PRIVIA</h1>
        <p className="subtitle">Luxury Villa Management</p>
      </div>
      
      <nav className="sidebar-nav">
        {menuSections.map((section) => (
          <div key={section.title} className="nav-section">
            <h3 className="section-title">{section.title}</h3>
            <ul className="nav-items">
              {section.items.map((item) => (
                <li key={item.id} className={`nav-item ${activeSection === item.id ? 'active' : ''}`}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/${item.id}`);
                    }}
                    className="nav-link"
                  >
                    <item.icon size={18} />
                    <span>{item.label}</span>
                    {item.badge && <span className="badge">{item.badge}</span>}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
};

// Header Component
const Header = () => (
  <header className="header">
    <div className="header-left">
      <div className="search-container">
        <Search size={18} />
        <input type="text" placeholder="Rechercher..." className="search-input" />
      </div>
    </div>
    
    <div className="header-right">
      <div className="header-icons">
        <button className="header-icon">
          <Bell size={20} />
          <span className="notification-badge">3</span>
        </button>
        <button className="header-icon">
          <MessageSquare size={20} />
          <span className="notification-badge">7</span>
        </button>
        <button className="header-icon">
          <CalendarIcon size={20} />
        </button>
      </div>
      
      <div className="user-menu">
        <div className="user-avatar">
          <User size={20} />
        </div>
        <div className="user-info">
          <span className="user-name">Marie Laurent</span>
          <span className="user-role">Gestionnaire</span>
        </div>
        <ChevronDown size={16} />
      </div>
    </div>
  </header>
);

// Dashboard Content Component
const DashboardContent = () => {
  const [dashboardData, setDashboardData] = useState({
    kpis: {},
    recentReservations: [],
    recentActivity: []
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await axios.get(`${API}/dashboard`);
        // Map backend response to frontend expected format
        const backendData = response.data;
        setDashboardData({
          kpis: backendData.kpis,
          recentReservations: backendData.recent_reservations || [],
          recentActivity: backendData.recent_activity || []
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Set mock data if API fails
        setDashboardData({
          kpis: {
            revenue: { value: "€2.4M", change: "vs mois dernier", trend: 15.3 },
            reservations: { value: "156", change: "ce mois", trend: 8.2 },
            occupancy: { value: "87%", change: "taux moyen", trend: 5.1 },
            vipClients: { value: "43", change: "clients actifs", trend: 12.0 },
            avgBasket: { value: "€15.4K", change: "par réservation", trend: -2.1 },
            npsScore: { value: "9.2", change: "satisfaction", trend: 3.4 }
          },
          recentReservations: [
            { id: 1, client: "Sophie Dubois", villa: "Villa Côte d'Azur", dates: "15-22 Sept", status: "Confirmé", amount: "€24,500" },
            { id: 2, client: "Jean-Marc Martin", villa: "Château Provence", dates: "20-27 Sept", status: "En attente", amount: "€18,900" },
            { id: 3, client: "Isabella Romano", villa: "Villa Toscane", dates: "25 Sept - 2 Oct", status: "Confirmé", amount: "€32,100" }
          ],
          recentActivity: [
            { time: "Il y a 2h", action: "Nouvelle réservation confirmée", details: "Villa Côte d'Azur - Sophie Dubois" },
            { time: "Il y a 4h", action: "Paiement reçu", details: "€24,500 - Réservation #1234" },
            { time: "Il y a 6h", action: "Client VIP ajouté", details: "Isabella Romano - Statut Platinum" }
          ]
        });
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="dashboard-content">
      {/* Action Bar */}
      <div className="action-bar">
        <button className="action-btn primary">
          <Plus size={18} />
          Nouvelle Réservation
        </button>
        <button className="action-btn secondary">
          <UserCheck size={18} />
          Ajouter Client VIP
        </button>
        <button className="action-btn secondary">
          <Building size={18} />
          Ajouter Villa
        </button>
        <button className="action-btn secondary">
          <FileText size={18} />
          Générer Rapport
        </button>
        <button className="action-btn ai">
          <TrendingUp size={18} />
          Assistant IA
        </button>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <KPICard
          title="Revenus Totaux"
          value={dashboardData.kpis?.revenue?.value || "€0"}
          change={dashboardData.kpis?.revenue?.change || ""}
          trend={dashboardData.kpis?.revenue?.trend || 0}
          icon={DollarSign}
          color="gold"
        />
        <KPICard
          title="Réservations Actives"
          value={dashboardData.kpis?.reservations?.value || "0"}
          change={dashboardData.kpis?.reservations?.change || ""}
          trend={dashboardData.kpis?.reservations?.trend || 0}
          icon={Calendar}
          color="blue"
        />
        <KPICard
          title="Taux d'Occupation"
          value={dashboardData.kpis?.occupancy?.value || "0%"}
          change={dashboardData.kpis?.occupancy?.change || ""}
          trend={dashboardData.kpis?.occupancy?.trend || 0}
          icon={Home}
          color="green"
        />
        <KPICard
          title="Clients VIP"
          value={dashboardData.kpis?.vipClients?.value || "0"}
          change={dashboardData.kpis?.vipClients?.change || ""}
          trend={dashboardData.kpis?.vipClients?.trend || 0}
          icon={Star}
          color="purple"
        />
        <KPICard
          title="Panier Moyen"
          value={dashboardData.kpis?.avgBasket?.value || "€0"}
          change={dashboardData.kpis?.avgBasket?.change || ""}
          trend={dashboardData.kpis?.avgBasket?.trend || 0}
          icon={Target}
          color="orange"
        />
        <KPICard
          title="Score NPS"
          value={dashboardData.kpis?.npsScore?.value || "0"}
          change={dashboardData.kpis?.npsScore?.change || ""}
          trend={dashboardData.kpis?.npsScore?.trend || 0}
          icon={Award}
          color="teal"
        />
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-container">
          <h3>Revenus Mensuels</h3>
          <div className="simple-chart">
            <div className="chart-bars">
              <div className="chart-bar" style={{height: '60%'}}><span>Jan</span></div>
              <div className="chart-bar" style={{height: '75%'}}><span>Fév</span></div>
              <div className="chart-bar" style={{height: '90%'}}><span>Mar</span></div>
              <div className="chart-bar" style={{height: '85%'}}><span>Avr</span></div>
              <div className="chart-bar" style={{height: '95%'}}><span>Mai</span></div>
              <div className="chart-bar" style={{height: '100%'}}><span>Jun</span></div>
            </div>
          </div>
        </div>

        <div className="chart-container">
          <h3>Top Destinations</h3>
          <div className="destinations-chart">
            <div className="destination-item">
              <span>Côte d'Azur</span>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: '85%'}}></div>
              </div>
              <span>85%</span>
            </div>
            <div className="destination-item">
              <span>Provence</span>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: '70%'}}></div>
              </div>
              <span>70%</span>
            </div>
            <div className="destination-item">
              <span>Toscane</span>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: '60%'}}></div>
              </div>
              <span>60%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Data Tables */}
      <div className="data-section">
        <div className="data-table-container">
          <div className="table-header">
            <h3>Réservations Récentes</h3>
            <button className="view-all-btn">Voir tout</button>
          </div>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Villa</th>
                  <th>Dates</th>
                  <th>Statut</th>
                  <th>Montant</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(dashboardData.recentReservations || []).map((reservation) => (
                  <tr key={reservation.id}>
                    <td>
                      <div className="client-info">
                        <div className="client-avatar">
                          {reservation.client.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span>{reservation.client}</span>
                      </div>
                    </td>
                    <td>
                      <div className="villa-info">
                        <MapPin size={14} />
                        <span>{reservation.villa}</span>
                      </div>
                    </td>
                    <td>{reservation.dates}</td>
                    <td>
                      <span className={`status ${reservation.status.toLowerCase().replace(' ', '-')}`}>
                        {reservation.status}
                      </span>
                    </td>
                    <td className="amount">{reservation.amount}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-icon">
                          <Eye size={14} />
                        </button>
                        <button className="action-icon">
                          <Edit size={14} />
                        </button>
                        <button className="action-icon">
                          <MoreHorizontal size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Activity Timeline */}
        <div className="activity-container">
          <h3>Activité Récente</h3>
          <div className="timeline">
            {(dashboardData.recentActivity || []).map((activity, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <div className="activity-time">{activity.time}</div>
                  <div className="activity-action">{activity.action}</div>
                  <div className="activity-details">{activity.details}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions Floating Button */}
      <div className="quick-actions">
        <button className="fab">
          <TrendingUp size={24} />
        </button>
      </div>
    </div>
  );
};

// AppContent Component (with routing)
const AppContent = () => {
  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-container">
        <Header />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<DashboardContent />} />
            <Route path="/dashboard" element={<DashboardContent />} />
            <Route path="/reservations-center" element={<ReservationsCenter />} />
            <Route path="/availability" element={<AvailabilityManagement />} />
            <Route path="/invoices-management" element={<InvoicesManagement />} />
            <Route path="/destination-settings" element={<DestinationSettings />} />
            <Route path="/bank-reconciliation" element={<BankReconciliation />} />
            <Route path="/cancellation-policy" element={<CancellationPolicy />} />
            <Route path="/commissions" element={<Commissions />} />
            <Route path="/communications-templates" element={<CommunicationsTemplates />} />
            <Route path="/communications-center" element={<CommunicationsCenter />} />
            <Route path="/concierge" element={<ConciergeManagement />} />
            <Route path="/contracts-legal" element={<ContractsLegal />} />
            <Route path="/destination-knowledge" element={<DestinationKnowledge />} />
            <Route path="/disputes-refunds" element={<DisputesRefunds />} />
            <Route path="/lead-management" element={<LeadManagement />} />
            <Route path="/myba-workflow" element={<MybaWorkflow />} />
            <Route path="/owners-management" element={<OwnersManagement />} />
            <Route path="/partners-management" element={<PartnersManagement />} />
            <Route path="/payments-management" element={<PaymentsManagement />} />
            <Route path="/restaurants-management" element={<RestaurantsManagement />} />
            <Route path="/settings-integrations" element={<SettingsIntegrations />} />
            <Route path="/staff-services" element={<StaffServices />} />
            <Route path="/users-management" element={<UsersManagement />} />
            <Route path="/villas-management" element={<VillasManagement />} />
            <Route path="/villa-workflow" element={<VillaWorkflow />} />
            <Route path="/vip-management" element={<VIPManagement />} />
            <Route path="/yachts-management" element={<YachtsManagement />} />
            <Route path="/yacht-communications" element={<YachtCommunications />} />
            <Route path="/clients" element={<ClientsBase />} />
            <Route path="/distribution" element={<Distribution />} />
            <Route path="/agencies-management" element={<AgenciesManagement />} />
            <Route path="*" element={
              <div className="placeholder-content">
                <h2>Page non trouvée</h2>
                <p>La page demandée n'existe pas.</p>
              </div>
            } />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <Router>
      <div className="App">
        <AppContent />
      </div>
    </Router>
  );
}

export default App;